import { useCallback, useEffect, useId, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type {
  JobAcceptedResponse,
  MeetingDetail,
  PostSlackContextRequest,
  PostSlackExclusionRequest,
  SetFilingRequest,
  SlackCitationDto,
  SlackContextPreviewResponse,
  SlackContextResponse,
  SlackExclusionsResponse,
  SlackFindingDto,
  SlackPriorReviewDto,
  TodoDto,
  UpdateTodoRequest,
} from '../../shared/api-types.js';
import {
  SLACK_FINDING_IMPORTANCE_LABELS,
  SLACK_OPPORTUNITY_TYPE_LABELS,
  type AiProvider,
  type SlackFindingImportance,
  type SlackFindingKind,
  type SlackPriorVerdict,
  type TodoStatus,
} from '../../shared/enums.js';
import { api, type ApiFailure } from '../api.js';
import { PATHS } from '../paths.js';
import { useMe } from '../shell/me-context.js';
import {
  Avatar,
  Banner,
  Button,
  Checkbox,
  Chip,
  Dialog,
  FailureView,
  Icon,
  IconButton,
  LinkButton,
  Menu,
  RelativeTime,
  Spinner,
  StateView,
  StatusPill,
  browserTimeZone,
  failureDetail,
  formatCount,
  formatList,
  formatDate,
  formatDateTime,
  inlineFailureText,
  pluralise,
  useRequest,
  useToast,
  type IconName,
} from '../ui/index.js';
import { ProjectChipList, ProjectFilingDialog, projectNames } from './project-filing.js';
import { slackCheckCaveatText } from './lib/climate.js';
import {
  groupSlackFindings,
  groupSlackPriorReview,
  slackActionItemsToShow,
  slackCheckExplainer,
  slackComparedWithText,
  slackCitationAuthor,
  slackCitationDescription,
  slackCitationsOf,
  slackContextView,
  slackDeniedText,
  slackDialogBlockedReason,
  slackDialogScope,
  slackDirectMessageOption,
  slackEmptyResultText,
  slackExcerptParts,
  slackExcludedText,
  slackFindingDomId,
  slackFindingTarget,
  slackInconsistencySides,
  slackMeetingLinks,
  slackNothingToReadText,
  slackPartialText,
  slackPermalinkHref,
  slackPriorEvidenceHref,
  slackPriorSubjectKind,
  slackPriorSubjectText,
  slackPriorVerdictLabel,
  slackProgress,
  slackProvenance,
  slackReviewChangedText,
  slackRunsLeftText,
  slackStillHoldsText,
  slackTodoDraft,
  slackWindowText,
  SLACK_CITATIONS_SHOWN,
  type SlackFindingGroup,
  type SlackMeetingLink,
  type SlackRequestOffer,
  type SlackSide,
} from './lib/slack-context.js';
import { filedUnderText, meetingTodoSearch, withTodoStatus, type TodoDraft } from './lib/todos.js';
import { useLazyRequest, type LazyRequestState } from './lib/use-lazy-request.js';
import { AddTodoDialog, DueDate, TodoWhy, useToday } from './todo-table.js';
import './pages.css';

// The meeting's Slack tab (ADR 0009, amended by ADR 0010; owner ruling 20260926-002133): what a Slack
// check of this meeting found — extra context, inconsistencies and opportunities, each citing the Slack
// messages behind it, with a provenance line whose account of how Slack was read comes from the check's
// counts and flags alone (R8 §7) — and "Look in Slack…", whose dialog is the per-run consent: it says
// the scope (everything the person can see in Slack, less the channels they left out, counted) and
// offers the one choice a run has, leaving out direct messages and group chats (R7). It is a
// related collection of the record page (06-modules §5.3 R3): lazy, deep-linkable to one finding
// (`?tab=slack#s-<id>`), with its own four states, and the server's `state` decides which one shows.
// Findings are plain text, never HTML; a link out goes only to a checked Slack message address, in a new
// tab that says so (D9). Above the breakdown sits "What to do" (owner rulings A1, A2, A6 of 2026-09-27):
// the action items the check distilled, already on the person's list as `slack` to-dos, each with Done,
// Dismiss and "Why?", so a busy person need not read the breakdown unless they want to look further. An
// opportunity's suggested to-do can still be added by hand through the person's own "Add a to-do" form
// (D4; ADR 0007) — unless an action item already rests on that finding, when the tab says so instead.
// A re-check's verdicts on the earlier check's conclusions (owner ruling A13 of 2026-09-28) sit between
// the provenance — which names the check compared with — and the breakdown, as "Since the last check".

/**
 * How often, and for how long, the tab looks again while a check waits or runs. Why the summary's twenty
 * minutes: a check reads Slack at Slack's own pace for up to nine minutes, its two judges included, then
 * waits up to five minutes for the AI provider (the calibration of 2026-09-27), with the queue's back-off
 * before a second attempt; anything longer is worth telling the person about.
 */
const SLACK_POLL_INTERVAL_MS = 5_000;
const SLACK_POLL_MAX = 240;

export interface SlackContextRequest {
  request: LazyRequestState<SlackContextResponse>;
  /** Polling gave up while a check was still waiting or running. */
  pollingExhausted: boolean;
  /** Look again now, and keep looking while a check waits or runs. */
  follow: () => void;
  /** Findings added as a to-do during this visit, by finding id: the to-do's id. */
  addedTodos: ReadonlyMap<string, string>;
  markTodoAdded: (findingId: string, todoId: string) => void;
}

/**
 * GET /api/meetings/:id/slack-context once the tab is wanted, then every few seconds while a check waits
 * or runs. `onSettled` fires when a check the tab was watching finishes, so the page can refresh what it
 * shows about it (the tab's count, the Details row).
 */
export function useSlackContext(meetingId: string, enabled: boolean, onSettled: () => void): SlackContextRequest {
  const path = `/api/meetings/${encodeURIComponent(meetingId)}/slack-context`;
  const request = useLazyRequest<SlackContextResponse>((signal) => api.get<SlackContextResponse>(path, { signal }), [path], enabled);
  const state = request.data?.state;
  const inFlight = state === 'queued' || state === 'running';
  const [polls, setPolls] = useState(0);
  const settled = useRef(onSettled);
  settled.current = onSettled;
  // Why a flag set on asking as well as on seeing a check run: a quick check can finish before the tab
  // ever sees it waiting, and its end must still reach the page.
  const watching = useRef(false);

  useEffect(() => {
    if (inFlight) watching.current = true;
    else if (watching.current && request.data) {
      watching.current = false;
      settled.current();
    }
  }, [inFlight, request.data]);

  useEffect(() => {
    if (!inFlight || polls >= SLACK_POLL_MAX) return;
    const handle = window.setTimeout(() => {
      setPolls((count) => count + 1);
      request.reload();
    }, SLACK_POLL_INTERVAL_MS);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inFlight, polls]);

  const follow = useCallback(() => {
    watching.current = true;
    setPolls(0);
    request.reload();
  }, [request.reload]);

  // Why here, above the tab, and only in memory: "Added to your to-do list" must survive a switch to another
  // tab and back, yet Calis keeps no link from a finding to the to-do made from it (design §9) — the
  // to-do is the person's own, and outlives the finding.
  const [addedTodos, setAddedTodos] = useState<ReadonlyMap<string, string>>(new Map());
  useEffect(() => setAddedTodos(new Map()), [meetingId]);
  const markTodoAdded = useCallback((findingId: string, todoId: string) => {
    setAddedTodos((current) => new Map(current).set(findingId, todoId));
  }, []);

  return { request, pollingExhausted: inFlight && polls >= SLACK_POLL_MAX, follow, addedTodos, markTodoAdded };
}

export interface SlackContextTabProps {
  meeting: MeetingDetail;
  context: SlackContextRequest;
  /** The Slack analysis floor (/api/me): a switched-off installation is not a missing grant. */
  floorMet: boolean;
  ai: AiProvider;
  /** Holds `slack_connect`: may stop reading a channel from one of its citations. */
  canChooseChannels: boolean;
  /** A finding was dismissed or restored: the page's own count of them moved. */
  onChanged: () => void;
  /** A to-do was added from a finding: the page's to-dos moved. */
  onTodoAdded: (created: TodoDto) => void;
  /** An action item was ticked, reopened, dismissed or restored under "What to do": the page's to-dos moved. */
  onTodoChanged: () => void;
}

const IMPORTANCE_ICONS: Readonly<Record<SlackFindingImportance, IconName>> = { high: 'arrow-up', medium: 'minus', low: 'arrow-down' };

function referenceOf(failure: ApiFailure) {
  return failure.correlationId ? <span className="text-xs muted"> Reference <code className="selectable">{failure.correlationId}</code></span> : null;
}

export function SlackContextTab({ meeting, context, floorMet, ai, canChooseChannels, onChanged, onTodoAdded, onTodoChanged }: SlackContextTabProps) {
  const { request, pollingExhausted, follow, addedTodos, markTodoAdded } = context;
  const location = useLocation();
  const toast = useToast();
  const timeZone = browserTimeZone();
  const path = `/api/meetings/${encodeURIComponent(meeting.id)}/slack-context`;
  const [dialogOpen, setDialogOpen] = useState(false);
  // Action items being ticked or dismissed under "What to do", by to-do id.
  const [itemBusy, setItemBusy] = useState<ReadonlySet<string>>(new Set());
  // Why a count of openings: each opening of "Look in Slack" is a fresh dialog, so it never shows the
  // channels, ticks or checks left of an earlier opening while this one's preview loads (the choice
  // may have changed since — "Stop reading" on a citation, or another tab).
  const [dialogOpening, setDialogOpening] = useState(0);
  const [busy, setBusy] = useState<ReadonlySet<string>>(new Set());
  const [revealed, setRevealed] = useState<ReadonlySet<SlackFindingKind>>(new Set());
  const [missing, setMissing] = useState<string | null>(null);
  // "Add as to-do…": the finding whose suggestion fills the form in, kept while the dialog closes.
  const [todoFrom, setTodoFrom] = useState<{ findingId: string; draft: TodoDraft } | null>(null);
  const [todoOpen, setTodoOpen] = useState(false);
  const [focusAdded, setFocusAdded] = useState<string | null>(null);
  const [focusRestored, setFocusRestored] = useState<string | null>(null);
  const target = useMemo(() => slackFindingTarget(location.hash), [location.hash]);
  const located = useRef<string | null>(null);
  const data = request.data;
  // Filing a finding by hand (ADR 0017, design §7.4): the Edit control on a card, offered only under the projects floor
  // (owner ruling 9; selection only — the route asks the floor itself). The finding whose projects are being chosen:
  const projectsOn = useMe().integrations.projects === 'on';
  // Whether a check asks the climate of what it reads (ADR 0019): the executive floor, as /api/me reports it — selection
  // only, for the dialog's words; the executor asks the floor itself.
  const climateAsked = useMe().integrations.executive === 'on';
  const [filingFinding, setFilingFinding] = useState<SlackFindingDto | null>(null);

  // Arriving by #s-<id>: bring the finding into view and identify it — a dismissed one is shown first —
  // or say it is not there (06-modules §5.5). Once found it is left alone, so a later poll never moves
  // the page under the reader.
  useEffect(() => {
    if (target === null || data === null || located.current === target) return;
    const finding = data.findings.find((candidate) => candidate.id === target);
    if (finding?.dismissed && !revealed.has(finding.kind)) {
      setRevealed((current) => new Set(current).add(finding.kind));
      return;
    }
    const element = document.getElementById(slackFindingDomId(target));
    if (!element) {
      setMissing(target);
      return;
    }
    located.current = target;
    setMissing(null);
    const frame = requestAnimationFrame(() => {
      element.scrollIntoView({ block: 'center', behavior: 'auto' });
      element.focus({ preventScroll: true });
    });
    return () => cancelAnimationFrame(frame);
  }, [target, data, revealed]);

  // Why: the dialog hands focus back to the button that opened it, which "Added to your to-do list" has just
  // replaced; the link to the new to-do takes it instead (01-foundations §7.2). This frame is asked for
  // after the dialog's own, so it lands last.
  useEffect(() => {
    if (focusAdded === null) return;
    const frame = requestAnimationFrame(() => {
      document.getElementById(`${slackFindingDomId(focusAdded)}-todo`)?.focus({ preventScroll: true });
      setFocusAdded(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [focusAdded, addedTodos]);

  // Why: a restored finding moves from the dismissed list back to its group's, and the Restore button
  // that had focus goes with the old item; focus follows the finding to its new place rather than
  // falling to the top of the page (01-foundations §7.2; 04-data: a moved item keeps focus). After the
  // commit, so it lands on the item in its new list, not the one about to be removed.
  useEffect(() => {
    if (focusRestored === null) return;
    const frame = requestAnimationFrame(() => {
      const element = document.getElementById(slackFindingDomId(focusRestored));
      element?.focus({ preventScroll: true });
      element?.scrollIntoView({ block: 'nearest' });
      setFocusRestored(null);
    });
    return () => cancelAnimationFrame(frame);
  }, [focusRestored, data]);

  const addAsTodo = (finding: SlackFindingDto): void => {
    const draft = slackTodoDraft(finding);
    if (draft === null) return;
    setTodoFrom({ findingId: finding.id, draft });
    setTodoOpen(true);
  };

  const setDismissed = async (finding: SlackFindingDto, dismissed: boolean): Promise<void> => {
    setBusy((current) => new Set(current).add(finding.id));
    const result = await api.post<SlackContextResponse>(`${path}/findings/${encodeURIComponent(finding.id)}/${dismissed ? 'dismiss' : 'restore'}`);
    setBusy((current) => {
      const next = new Set(current);
      next.delete(finding.id);
      return next;
    });
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t {dismissed ? 'dismiss' : 'restore'} the finding. {failureDetail(result)}</>);
      if (result.code === 'not_found') request.reload();
      return;
    }
    request.setData(result.data);
    onChanged();
    if (dismissed) {
      // The finding leaves the list: focus goes to its group, never to the top of the page.
      requestAnimationFrame(() => document.getElementById(`slack-group-${finding.kind}`)?.focus({ preventScroll: true }));
      toast.success('Finding dismissed. It stays dismissed on later checks.', { action: { label: 'Undo', onClick: () => void setDismissed(finding, false) } });
    } else {
      setFocusRestored(finding.id);
      toast.success('Finding restored.');
    }
  };

  // A filing is the person's sorting of the finding (owner ruling 3): the route answers the whole tab, which replaces
  // what is shown; the toast names the projects and offers Undo, which sends the previous set back the same way
  // (03-components §4.2 prefers undo to confirm). A finding gone since (404) reads the tab again.
  const filingPath = (finding: SlackFindingDto): string => `${path}/findings/${encodeURIComponent(finding.id)}/projects`;
  const announceFiling = (finding: SlackFindingDto, response: SlackContextResponse, previous: string[]): void => {
    const filed = response.findings.find((candidate) => candidate.id === finding.id);
    toast.success(filedUnderText(projectNames(filed?.projects ?? [])), {
      key: `finding-filing-${finding.id}`,
      action: {
        label: 'Undo',
        onClick: () => {
          void api.post<SlackContextResponse>(filingPath(finding), { projectIds: previous } satisfies SetFilingRequest).then((result) => {
            if (!result.ok) {
              toast.error(<>We couldn&rsquo;t undo that filing. {failureDetail(result)}</>);
              if (result.code === 'not_found') request.reload();
              return;
            }
            request.setData(result.data);
            onChanged();
          });
        },
      },
    });
  };

  // Done, Dismiss and their Undo under "What to do": the same PATCH as the to-do list, the row updated
  // at once and the change offered back in the toast (03-components §4.2 prefers undo to confirm). The
  // to-do is the person's own from the moment it is made, so ticking it here is ticking it anywhere.
  const setItemStatus = async (todo: TodoDto, status: TodoStatus, options: { announce: string; undoTo: TodoStatus }): Promise<void> => {
    const previous = todo.status;
    const optimisticAt = new Date().toISOString();
    const patch = (next: TodoStatus, at: string) => request.setData((current) => (current ? { ...current, actionItems: withTodoStatus(current.actionItems, todo.id, next, at) } : current));
    setItemBusy((current) => new Set(current).add(todo.id));
    patch(status, optimisticAt);
    const result = await api.patch<TodoDto>(`/api/todos/${encodeURIComponent(todo.id)}`, { status } satisfies UpdateTodoRequest);
    setItemBusy((current) => {
      const next = new Set(current);
      next.delete(todo.id);
      return next;
    });
    if (!result.ok) {
      patch(previous, todo.updatedAt);
      toast.error(<>We couldn&rsquo;t update that to-do. {failureDetail(result)}</>);
      if (result.code === 'not_found') request.reload();
      return;
    }
    toast.success(options.announce, {
      key: `todo-${todo.id}`,
      action: {
        label: 'Undo',
        onClick: () => {
          void api.patch<TodoDto>(`/api/todos/${encodeURIComponent(todo.id)}`, { status: options.undoTo } satisfies UpdateTodoRequest).then((undo) => {
            if (!undo.ok) {
              toast.error(<>We couldn&rsquo;t undo that. {failureDetail(undo)}</>);
              return;
            }
            request.reload();
            onTodoChanged();
          });
        },
      },
    });
    request.reload();
    onTodoChanged();
  };

  const toggleItemDone = (todo: TodoDto): void => {
    if (todo.status === 'done') void setItemStatus(todo, 'open', { announce: 'Reopened', undoTo: 'done' });
    else void setItemStatus(todo, 'done', { announce: 'Marked as completed', undoTo: todo.status });
  };

  const dismissItem = (todo: TodoDto): void => {
    void setItemStatus(todo, 'dismissed', { announce: 'Dismissed. It stays on your to-do list under Dismissed; later checks will not add it again.', undoTo: todo.status });
  };

  // "Stop reading #channel" (a citation's menu): adds the channel to "Channels Calis won't read" (owner
  // ruling R7), as Connections does — a channel the person never joined included, named by this
  // citation — and is undoable from the toast.
  const stopReading = async (citation: SlackCitationDto): Promise<void> => {
    const channelId = citation.channelId;
    if (channelId === null) return;
    const label = citation.conversationLabel;
    const body: PostSlackExclusionRequest = { conversationId: channelId, citation: { meetingId: meeting.id, citationId: citation.id } };
    const result = await api.post<SlackExclusionsResponse>('/api/connections/slack/exclusions', body);
    if (!result.ok) {
      toast.error(<>We couldn&rsquo;t stop Calis reading {label}. {failureDetail(result)}</>);
      return;
    }
    request.reload();
    if (!result.data.changed) {
      toast.info(`${label} is already left out: Calis does not read it.`);
      return;
    }
    toast.success(`Calis won’t read ${label} any more; change this in Connections. What it found there stays until the next check.`, {
      action: {
        label: 'Undo',
        onClick: () => {
          void api.delete<SlackExclusionsResponse>(`/api/connections/slack/exclusions/${encodeURIComponent(channelId)}`).then((undone) => {
            if (undone.ok) toast.success(`Calis reads ${label} again.`);
            else toast.error(<>We couldn&rsquo;t read {label} again. {failureDetail(undone)}</>);
            request.reload();
          });
        },
      },
    });
  };

  if (request.status === 'idle' || (request.status === 'loading' && data === null)) {
    return <StateView kind="loading" label="Loading the Slack check" minHeight="16rem" />;
  }
  if (data === null) {
    return request.error ? <FailureView failure={request.error} what="the Slack check" onRetry={request.reload} minHeight="10rem" /> : null;
  }

  const view = slackContextView(data, { floorMet });
  const openDialog = (): void => {
    setDialogOpening((count) => count + 1);
    setDialogOpen(true);
  };
  const offer = view.request ? <RequestOffer offer={view.request} onOpen={openDialog} /> : undefined;
  const results = view.findings !== null && data.analysis !== null ? (
    <SlackResults
      response={data}
      findings={view.findings}
      meeting={meeting}
      timeZone={timeZone}
      target={target}
      revealed={revealed}
      onReveal={(kind, shown) => setRevealed((current) => {
        const next = new Set(current);
        if (shown) next.add(kind);
        else next.delete(kind);
        return next;
      })}
      busy={busy}
      onDismiss={(finding) => void setDismissed(finding, true)}
      onRestore={(finding) => void setDismissed(finding, false)}
      canChooseChannels={canChooseChannels}
      onStopReading={(citation) => void stopReading(citation)}
      addedTodos={addedTodos}
      onAddTodo={addAsTodo}
      {...(projectsOn ? { onFile: setFilingFinding } : {})}
      offer={view.state === 'ready' ? offer : undefined}
    />
  ) : null;

  const body = (() => {
    switch (view.state) {
      case 'not-configured':
        return (
          <StateView
            kind="not-configured"
            title="Slack checks are not switched on in this installation"
            description="This installation does not have both Slack and an AI provider configured. Nothing is failing; an operator can enable them."
            minHeight="10rem"
          />
        );
      case 'denied': {
        const text = slackDeniedText(data.missingCapabilities);
        return <StateView kind="denied" title={text.title} description={text.description} minHeight="10rem" />;
      }
      case 'not-connected':
        return (
          <StateView
            kind="empty"
            icon="plug"
            title="Connect Slack to see related conversations"
            description={data.connectionStatus === 'revoked'
              ? 'Slack no longer accepts Calis’s access, so Calis cannot look in Slack for this meeting. Connect Slack again from Connections.'
              : 'Calis can check this meeting against everything you can see in Slack, less anything you leave out. Connect your Slack account first.'}
            action={<LinkButton to={`${PATHS.connections}#slack-connection`} variant="secondary" icon="plug">Go to Connections</LinkButton>}
            minHeight="10rem"
          />
        );
      case 'no-sources':
        return (
          <StateView
            kind="empty"
            icon="list"
            title="Calis has nothing it may read in Slack"
            description={slackNothingToReadText(data.nothingToRead)}
            action={<LinkButton to={`${PATHS.connections}#slack-connection`} variant="secondary" icon="list">Go to Connections</LinkButton>}
            minHeight="10rem"
          />
        );
      case 'needs-summary':
        return (
          <StateView
            kind="empty"
            icon="sparkles"
            title="No summary to check against yet"
            description="Generate the summary first."
            minHeight="10rem"
          />
        );
      case 'never':
        return <SlackNothingChecked skippedCheck={data.skippedCheck} offer={offer} />;
      case 'pending':
      case 'running': {
        const progress = slackProgress(data);
        return (
          <>
            <div className="summary-pending" role="status" aria-live="polite">
              <Spinner size={20} label={progress?.word ?? 'Running'} />
              <div className="stack stack--1">
                <p><strong>{progress?.word}</strong> · {progress?.text}…</p>
                <p className="text-sm secondary">
                  A check usually takes a minute or two. You can leave; the page refreshes by itself.
                </p>
                {pollingExhausted ? (
                  <p className="text-sm">
                    It is taking longer than usual. <Button variant="link" size="sm" onClick={follow}>Check again</Button>
                  </p>
                ) : null}
              </div>
            </div>
            {results ? <p className="text-sm muted">The previous check’s result is below.</p> : null}
            {results}
          </>
        );
      }
      case 'failed': {
        const run = data.latestRun;
        return (
          <>
            <StateView
              kind="error"
              title="Calis could not check Slack"
              description={
                <>
                  <p>{run?.errorMessage ?? 'The last check failed.'}</p>
                  {run?.errorCode ? <p className="text-xs muted">Code <code className="selectable">{run.errorCode}</code>{run.completedAt ? <> · <RelativeTime value={run.completedAt} mode="both" /></> : null}</p> : null}
                </>
              }
              action={offer}
              minHeight="10rem"
            />
            {results ? <p className="text-sm muted">The previous check’s result is below.</p> : null}
            {results}
          </>
        );
      }
      case 'unreadable':
        return (
          <StateView
            kind="error"
            title="These results can no longer be opened"
            description="This installation’s Slack key changed, so Calis cannot open what it found for this meeting. Look in Slack again."
            action={offer}
            minHeight="10rem"
          />
        );
      case 'ready':
      default:
        // Why a quiet line above the result (the owner's ruling Q6(b) of escalation 20261005-200526; 05-content §6): a
        // re-check the person's own switch skipped after this result owes a record visible to them alone; the result
        // stands (R3), and the server sends `skippedCheck` here only when the skip came after it.
        return data.skippedCheck?.reason === 'auto_check_off' ? (
          <>
            <SlackNotCheckedAgain skippedCheck={data.skippedCheck} />
            {results}
          </>
        ) : results;
    }
  })();

  // Why whatever the state: the action items are the person's to-dos, kept whatever became of the check
  // that made them (owner ruling A4) — so they stand above a result that can no longer be opened too.
  const actionItems = slackActionItemsToShow(data.actionItems);

  return (
    <div className="slack-context stack">
      {missing !== null ? (
        <Banner tone="info" role="status">
          This finding is no longer available; it may have been replaced by a newer check, or removed when Slack was disconnected.
        </Banner>
      ) : null}
      {request.status === 'error' && request.error ? (
        <Banner tone="warning" title="The Slack check could not be refreshed" actions={<Button variant="secondary" size="sm" icon="refresh" onClick={request.reload}>Try again</Button>}>
          {/* Why `inlineFailureText` (ruling Q11): a refused refresh names the pop-ups' next step. */}
          <p>{inlineFailureText(request.error)}{referenceOf(request.error)}</p>
        </Banner>
      ) : null}
      {view.notices.map((notice) => (
        <Banner
          key={notice.id}
          tone={notice.tone}
          title={notice.title}
          actions={notice.toConnections ? <LinkButton to={`${PATHS.connections}#slack-connection`} variant="secondary" size="sm" icon="plug">Go to Connections</LinkButton> : undefined}
        >
          <p>{notice.text}</p>
        </Banner>
      ))}
      {actionItems.length > 0 ? <WhatToDo items={actionItems} busy={itemBusy} onToggleDone={toggleItemDone} onDismiss={dismissItem} /> : null}
      {body}
      <LookInSlackDialog
        key={dialogOpening}
        open={dialogOpen}
        meeting={meeting}
        ai={ai}
        climate={climateAsked}
        onClose={() => setDialogOpen(false)}
        onQueued={(accepted) => {
          setDialogOpen(false);
          toast.success(accepted.alreadyQueued ? 'Calis is already looking in Slack for this meeting.' : 'Calis is looking in Slack. What it finds appears here.');
          follow();
        }}
      />
      <AddTodoDialog
        open={todoOpen}
        onClose={() => setTodoOpen(false)}
        fixedMeetingId={meeting.id}
        initial={todoFrom?.draft}
        onCreated={(created) => {
          setTodoOpen(false);
          if (todoFrom) {
            markTodoAdded(todoFrom.findingId, created.id);
            setFocusAdded(todoFrom.findingId);
          }
          toast.success('To-do added.');
          onTodoAdded(created);
        }}
      />
      {projectsOn && filingFinding ? (
        <ProjectFilingDialog<SlackContextResponse>
          open
          title="Change projects"
          description={<>The projects <strong>{filingFinding.title}</strong> is filed under. Your choice stands when Calis checks this meeting against Slack again.</>}
          initial={filingFinding.projects.map((project) => project.id)}
          onClose={() => setFilingFinding(null)}
          onApply={(projectIds) => api.post<SlackContextResponse>(filingPath(filingFinding), { projectIds } satisfies SetFilingRequest)}
          onApplied={(response, change) => {
            setFilingFinding(null);
            request.setData(response);
            onChanged();
            announceFiling(filingFinding, response, change.previous);
          }}
        />
      ) : null}
    </div>
  );
}

// --- "What to do" ------------------------------------------------------------------------------------

interface WhatToDoProps {
  /** The meeting's action items, open and done, in the order the check made them. */
  items: TodoDto[];
  busy: ReadonlySet<string>;
  onToggleDone: (todo: TodoDto) => void;
  onDismiss: (todo: TodoDto) => void;
}

/**
 * The compact list above the breakdown (owner rulings A1, A2, A6): each action item with Done, its due
 * date and who it names, Dismiss, and "Why?" — the reason and the way to the finding. Done items stay,
 * ticked, so the list shows what is left; a dismissed one goes, as it does from the open list.
 */
export function WhatToDo({ items, busy, onToggleDone, onDismiss }: WhatToDoProps) {
  const today = useToday();
  const headingId = 'slack-what-to-do';
  const open = items.filter((todo) => todo.status === 'open').length;
  return (
    <section className="what-to-do stack stack--3" aria-labelledby={headingId}>
      <div className="stack stack--1">
        <h3 id={headingId} className="slack-group__heading">
          What to do <span className="slack-group__count">{open > 0 ? formatCount(open) : 'All completed'}</span>
        </h3>
        <p className="text-sm secondary">
          These are already on your to-do list, marked From Slack.
        </p>
      </div>
      <ol className="what-to-do__list" aria-label="Action items from Slack">
        {items.map((todo) => (
          <li key={todo.id} className={['what-to-do__item', todo.status === 'done' ? 'is-done' : ''].filter(Boolean).join(' ')} data-action-item={todo.id}>
            <div className="what-to-do__row">
              <Checkbox
                className="todo-done what-to-do__done"
                checked={todo.status === 'done'}
                disabled={busy.has(todo.id)}
                onChange={() => onToggleDone(todo)}
                label={<span className={['todo-text', todo.status === 'done' ? 'is-done' : ''].filter(Boolean).join(' ')}>{todo.text}</span>}
                description={todo.dueDate || todo.responsible ? (
                  <span className="what-to-do__meta">
                    {todo.dueDate ? <DueDate dueDate={todo.dueDate} status={todo.status} today={today} /> : null}
                    {todo.dueDate && todo.responsible ? <span aria-hidden="true"> · </span> : null}
                    {todo.responsible ? <span>{todo.responsible}</span> : null}
                  </span>
                ) : undefined}
              />
              <div className="what-to-do__actions cluster">
                <Button variant="ghost" size="sm" icon="x" disabled={busy.has(todo.id)} onClick={() => onDismiss(todo)}>
                  Dismiss<span className="visually-hidden">: {todo.text}</span>
                </Button>
              </div>
            </div>
            <TodoWhy todo={todo} />
          </li>
        ))}
      </ol>
    </section>
  );
}

// --- "Since the last check" (owner ruling A13) ---------------------------------------------------------

/** A re-check the person's own switch skipped after the result shown (the owner's ruling Q6(b)), said quietly beside it. */
export const SLACK_NOT_CHECKED_AGAIN_TEXT = 'Not checked again: automatic checks are off';

/** The quiet line above a result: the re-check the person's switch skipped after it, with when; nothing for any other. */
export function SlackNotCheckedAgain({ skippedCheck }: { skippedCheck: SlackContextResponse['skippedCheck'] }) {
  if (skippedCheck?.reason !== 'auto_check_off') return null;
  return <p className="text-sm muted">{SLACK_NOT_CHECKED_AGAIN_TEXT} · <RelativeTime value={skippedCheck.at} mode="both" /></p>;
}

/**
 * The tab before any check of the meeting. Why it can say "not checked: automatic checks are off" (the owner's ruling O2
 * of 2026-10-05; 05-content §6): a check the person's own switch skipped owes a record visible to them alone, and the
 * server sends `skippedCheck` on this, the meeting owner's own route, and nowhere else.
 */
export function SlackNothingChecked({ skippedCheck, offer }: { skippedCheck: SlackContextResponse['skippedCheck']; offer?: ReactNode }) {
  return (
    <StateView
      kind="empty"
      icon="search"
      title={skippedCheck?.reason === 'auto_check_off' ? 'Not checked: automatic checks are off' : 'Nothing checked yet'}
      // Why so short: the window, the scope, what is sent and what is kept are the dialog's to say (R7, R8) — the per-run consent its button opens.
      description="Look in Slack to compare this meeting with the conversations around it. What there is to do goes onto your to-do list, marked From Slack."
      action={offer}
      minHeight="10rem"
    />
  );
}

/** Each verdict paired with a glyph as well as a tone: never colour alone (01-foundations §7.3). */
const VERDICT_PRESENTATION: Readonly<Record<SlackPriorVerdict, { tone: 'success' | 'info' | 'neutral' | 'warning'; icon: IconName }>> = {
  resolved: { tone: 'success', icon: 'check' },
  overtaken: { tone: 'info', icon: 'clock' },
  no_longer_relevant: { tone: 'neutral', icon: 'minus' },
  unclear: { tone: 'warning', icon: 'help-circle' },
  still_holds: { tone: 'neutral', icon: 'dot' },
};

interface SinceLastCheckProps {
  meetingId: string;
  /** The verdicts, in the order the check gave them. */
  entries: readonly SlackPriorReviewDto[];
}

/**
 * What a re-check made of the earlier check's conclusions (owner ruling A13), between the provenance
 * — which names the check compared with — and the breakdown: each conclusion Slack has since settled,
 * overtaken or made moot, with the reason and the way to the finding of this check that shows it; what
 * the check could not judge, said plainly; and what still holds, folded under a count — nothing about
 * it changed, so it need not be read again. A to-do the check dismissed on such a verdict is named here
 * with the way to it; the to-do list shows it under Dismissed with the reason as its note, and Restore.
 */
export function SinceLastCheck({ meetingId, entries }: SinceLastCheckProps) {
  if (entries.length === 0) return null;
  const groups = groupSlackPriorReview(entries);
  const headingId = 'slack-since-last-check';
  const line = (entry: SlackPriorReviewDto, key: number) => <ReviewLine key={key} entry={entry} meetingId={meetingId} />;
  return (
    <section className="slack-review stack stack--3" aria-labelledby={headingId}>
      <div className="stack stack--1">
        <h3 id={headingId} className="slack-group__heading">
          Since the last check <span className="slack-group__count">{slackReviewChangedText(groups.changed.length)}</span>
        </h3>
        <p className="text-sm secondary">
          A to-do found settled or no longer needed is dismissed on your to-do list, where you can restore it.
        </p>
      </div>
      {groups.changed.length > 0 ? (
        <ol className="slack-review__list" aria-label="Earlier conclusions that changed">
          {groups.changed.map(line)}
        </ol>
      ) : null}
      {groups.unclear.length > 0 ? (
        <ol className="slack-review__list" aria-label="Earlier conclusions Calis could not judge">
          {groups.unclear.map(line)}
        </ol>
      ) : null}
      {groups.stillHolds.length > 0 ? (
        <details className="disclosure slack-review__still">
          <summary>
            <Icon name="chevron-down" size={16} />
            {slackStillHoldsText(groups.stillHolds.length)}
          </summary>
          <div className="disclosure__body">
            <ol className="slack-review__list" aria-label="Earlier conclusions that still hold">
              {groups.stillHolds.map(line)}
            </ol>
          </div>
        </details>
      ) : null}
    </section>
  );
}

function ReviewLine({ entry, meetingId }: { entry: SlackPriorReviewDto; meetingId: string }) {
  const { tone, icon } = VERDICT_PRESENTATION[entry.verdict];
  const subject = slackPriorSubjectText(entry.prior);
  const evidence = slackPriorEvidenceHref(meetingId, entry);
  return (
    <li className="slack-review__item" data-verdict={entry.verdict}>
      <div className="slack-review__head cluster">
        <StatusPill tone={tone} icon={icon}>{slackPriorVerdictLabel(entry.verdict)}</StatusPill>
        <span className="text-xs muted">{slackPriorSubjectKind(entry.prior)}</span>
        {/* Why said: a carried verdict is an earlier check's, not this one's; the heading would otherwise claim it. */}
        {entry.carried ? <span className="text-xs muted">· settled by an earlier check</span> : null}
      </div>
      <p className="slack-review__subject">{subject}</p>
      <p className="slack-review__reason text-sm secondary">{entry.reason}</p>
      {/* Why the links stand on a line of their own: the reason is prose and keeps its leading; each
          link is a box at least 44px tall on touch (pages.css), so the hit area is met without spacing
          the sentence out (01-foundations §6.4.1). */}
      {evidence || entry.prior.type === 'todo' ? (
        <p className="slack-review__links cluster text-sm">
          {evidence ? (
            // Why a standalone target: the glyph is the ruling's "›"; the name says where it goes.
            <Link to={evidence} className="slack-review__link hit-area">
              See the finding<span className="visually-hidden"> behind “{subject}”</span> <span aria-hidden="true">›</span>
            </Link>
          ) : null}
          {entry.prior.type === 'todo' ? (
            <Link to={{ search: meetingTodoSearch(entry.prior.todoId) }} className="slack-review__link hit-area">
              View the to-do<span className="visually-hidden">: {subject}</span> <span aria-hidden="true">›</span>
            </Link>
          ) : null}
        </p>
      ) : null}
    </li>
  );
}

function RequestOffer({ offer, onOpen }: { offer: SlackRequestOffer; onOpen: () => void }) {
  // Why no disabled button: the reason is the answer, shown where the control would be (03-components §2).
  if (offer.blockedReason !== null) return <p className="text-sm muted">{offer.blockedReason}</p>;
  return <Button variant="secondary" icon="search" onClick={onOpen}>{offer.label}</Button>;
}

// --- The result ------------------------------------------------------------------------------------

interface SlackResultsProps {
  response: SlackContextResponse;
  findings: SlackFindingDto[];
  meeting: MeetingDetail;
  timeZone: string;
  target: string | null;
  revealed: ReadonlySet<SlackFindingKind>;
  onReveal: (kind: SlackFindingKind, shown: boolean) => void;
  busy: ReadonlySet<string>;
  onDismiss: (finding: SlackFindingDto) => void;
  onRestore: (finding: SlackFindingDto) => void;
  canChooseChannels: boolean;
  onStopReading: (citation: SlackCitationDto) => void;
  /** Findings added as a to-do during this visit: the to-do's id by finding id. */
  addedTodos: ReadonlyMap<string, string>;
  onAddTodo: (finding: SlackFindingDto) => void;
  /** Filing a finding by hand (ADR 0017): present only under the projects floor; absent, a card carries its chips and no Edit control. */
  onFile?: (finding: SlackFindingDto) => void;
  /** "Look in Slack again…", under a finished result. */
  offer: ReactNode;
}

function SlackResults({ response, findings, meeting, timeZone, target, revealed, onReveal, busy, onDismiss, onRestore, canChooseChannels, onStopReading, addedTodos, onAddTodo, onFile, offer }: SlackResultsProps) {
  const analysis = response.analysis;
  if (analysis === null) return null;
  const provenance = slackProvenance(analysis, timeZone);
  const partial = slackPartialText(analysis);
  const groups = groupSlackFindings(findings);
  const checkedAt = analysis.checkedAt ?? analysis.completedAt;
  const comparedWith = slackComparedWithText(response.comparedWith, timeZone);

  return (
    <div className="stack">
      <div className="stack stack--1">
        <p className="summary-provenance">
          {analysis.isDemo ? <Chip tone="warning" icon="bolt" title="Found by the demo analyser, not by an AI model">Demo</Chip> : null}
          {analysis.basedOnEarlierSummary ? <Chip tone="neutral" icon="clock" title="The meeting's summary was generated again after this check">Based on an earlier summary</Chip> : null}
          {/* One run of text, so it wraps as a sentence rather than as separate pieces. */}
          <span>
            {provenance.read}
            {provenance.how ? ` · ${provenance.how}` : ''}
            {provenance.attendees ? ` · ${provenance.attendees}` : ''}
            {/* Why whole alternatives (05-content §8; the Phase 1c audit's D30): "checked …" and " by …" were spliced on by
                condition. The " · " pieces are separate facts in a list, not a sentence. */}
            {' · '}
            {checkedAt
              ? provenance.by ? <>checked <RelativeTime value={checkedAt} /> by {provenance.by}</> : <>checked <RelativeTime value={checkedAt} /></>
              : provenance.by ? `checked by ${provenance.by}` : 'checked'}
            {comparedWith ? ` · ${comparedWith}` : ''}
          </span>
        </p>
        {/* Who found it is the provenance's to say, just above; this line carries the caution and the privacy fact — and,
            exactly when the check shown was counted into the organisation pulse (ADR 0019; design §2.14 item 7), that. */}
        <p className="text-xs muted">{slackCheckCaveatText(response.climateCounted)}</p>
      </div>
      {partial ? <Banner tone="warning" title="This check is incomplete"><p>{partial}</p></Banner> : null}
      {/* After "What to do" (which sits above the whole result) and before the breakdown (owner ruling A13). */}
      {response.priorReview ? <SinceLastCheck meetingId={meeting.id} entries={response.priorReview} /> : null}
      {findings.length === 0 ? (
        <StateView kind="empty" icon="check" title="Nothing from Slack added to this meeting" description={slackEmptyResultText(analysis, timeZone)} action={offer} minHeight="10rem" />
      ) : (
        <>
          {groups.map((group) => (
            <SlackGroup
              key={group.kind}
              group={group}
              citations={response.citations}
              meeting={meeting}
              earlierSummary={analysis.basedOnEarlierSummary}
              timeZone={timeZone}
              target={target}
              showDismissed={revealed.has(group.kind)}
              onShowDismissed={(shown) => onReveal(group.kind, shown)}
              busy={busy}
              onDismiss={onDismiss}
              onRestore={onRestore}
              canChooseChannels={canChooseChannels}
              onStopReading={onStopReading}
              addedTodos={addedTodos}
              onAddTodo={onAddTodo}
              {...(onFile ? { onFile } : {})}
            />
          ))}
          {offer ? <div className="cluster">{offer}</div> : null}
        </>
      )}
    </div>
  );
}

interface SlackGroupProps extends Pick<SlackResultsProps, 'meeting' | 'timeZone' | 'target' | 'busy' | 'onDismiss' | 'onRestore' | 'canChooseChannels' | 'onStopReading' | 'addedTodos' | 'onAddTodo' | 'onFile'> {
  group: SlackFindingGroup;
  citations: SlackCitationDto[];
  earlierSummary: boolean;
  showDismissed: boolean;
  onShowDismissed: (shown: boolean) => void;
}

function SlackGroup({ group, showDismissed, onShowDismissed, ...item }: SlackGroupProps) {
  const headingId = `slack-group-${group.kind}`;
  const dismissedId = `slack-dismissed-${group.kind}`;
  const count = group.visible.length;
  return (
    <section className="slack-group stack stack--2" aria-labelledby={headingId}>
      <h3 id={headingId} tabIndex={-1} className="slack-group__heading">
        {/* Why "All dismissed": a group whose every finding was set aside did find them; "None found" would be untrue above "Show dismissed". */}
        {group.heading} <span className="slack-group__count">{count > 0 ? formatCount(count) : group.dismissed.length > 0 ? 'All dismissed' : 'None found'}</span>
      </h3>
      {count > 0 ? (
        <ol className="slack-findings">
          {group.visible.map((finding) => <SlackFindingItem key={finding.id} finding={finding} {...item} />)}
        </ol>
      ) : null}
      {group.dismissed.length > 0 ? (
        <div className="stack stack--2">
          <div>
            <Button variant="link" size="sm" icon={showDismissed ? 'chevron-up' : 'chevron-down'} aria-expanded={showDismissed} aria-controls={showDismissed ? dismissedId : undefined} onClick={() => onShowDismissed(!showDismissed)}>
              {showDismissed ? 'Hide dismissed' : 'Show dismissed'} ({formatCount(group.dismissed.length)})
            </Button>
          </div>
          {showDismissed ? (
            <ol id={dismissedId} className="slack-findings" aria-label={`Dismissed findings: ${group.heading}`}>
              {group.dismissed.map((finding) => <SlackFindingItem key={finding.id} finding={finding} {...item} />)}
            </ol>
          ) : null}
        </div>
      ) : null}
    </section>
  );
}

export interface SlackFindingItemProps extends Omit<SlackGroupProps, 'group' | 'showDismissed' | 'onShowDismissed'> {
  finding: SlackFindingDto;
}

// Exported, like WhatToDo, for the rendering tests; the tab is its only other reader.
export function SlackFindingItem({ finding, citations, meeting, earlierSummary, timeZone, target, busy, onDismiss, onRestore, canChooseChannels, onStopReading, addedTodos, onAddTodo, onFile }: SlackFindingItemProps) {
  const domId = slackFindingDomId(finding.id);
  const highlighted = target === finding.id;
  const cited = slackCitationsOf(finding, citations);
  const shown = cited.slice(0, SLACK_CITATIONS_SHOWN);
  const more = cited.slice(SLACK_CITATIONS_SHOWN);
  const sides = slackInconsistencySides(finding, cited, meeting.startedAt);
  const links = slackMeetingLinks(finding, { earlierSummary });
  const citationProps = { timeZone, canChooseChannels, onStopReading };
  const addedTodo = addedTodos.get(finding.id) ?? null;
  // An action item the check made from this finding (owner ruling A6): the offer to add one by hand
  // gives way to the fact, with the way to the to-do — never a disabled button (03-components §2).
  const [listedTodo = null] = finding.todoIds;
  const canAddTodo = slackTodoDraft(finding) !== null;

  return (
    <li
      id={domId}
      tabIndex={-1}
      aria-labelledby={`${domId}-title`}
      aria-current={highlighted ? 'true' : undefined}
      className={['slack-finding', 'record-anchor', highlighted ? 'is-highlighted' : '', finding.dismissed ? 'is-dismissed' : ''].filter(Boolean).join(' ')}
    >
      {/* No kind pill: the group heading above every card already names its kind. */}
      <div className="slack-finding__pills cluster">
        {finding.opportunityType ? <StatusPill tone="neutral" icon="dot">{SLACK_OPPORTUNITY_TYPE_LABELS[finding.opportunityType]}</StatusPill> : null}
        <StatusPill tone="neutral" icon={IMPORTANCE_ICONS[finding.importance]}>{`${SLACK_FINDING_IMPORTANCE_LABELS[finding.importance]} importance`}</StatusPill>
        {finding.confidence === 'low' ? <StatusPill tone="neutral" icon="help-circle" title="The check was not sure of this one">Possible</StatusPill> : null}
        {finding.isNew ? <StatusPill tone="info" icon="sparkles">New since last check</StatusPill> : null}
        {finding.dismissed ? <StatusPill tone="neutral" icon="minus">Dismissed</StatusPill> : null}
      </div>
      <h4 id={`${domId}-title`} className="slack-finding__title">{finding.title}</h4>
      <p className="slack-finding__detail">{finding.detail}</p>
      {sides ? (
        <div className="slack-sides">
          <SideBlock label="In the meeting" side={sides.meeting} links={links} timeZone={timeZone} />
          <SideBlock label="In Slack" side={sides.slack} links={[]} timeZone={timeZone} />
        </div>
      ) : links.length > 0 ? (
        <MeetingLinks links={links} />
      ) : null}
      {finding.people.length > 0 ? <p className="text-sm"><span className="secondary">People: </span>{formatList(finding.people)}</p> : null}
      {finding.suggestedTodo ? <p className="text-sm"><span className="secondary">Suggested to-do: </span>{finding.suggestedTodo}</p> : null}
      {/* The finding's projects (ADR 0017, owner ruling 3): the chips, and — under the floor — the one control that changes them. */}
      {onFile || finding.projects.length > 0 ? (
        <p className="slack-finding__projects text-sm cluster">
          <span className="secondary">Projects: </span>
          <ProjectChipList projects={finding.projects} />
          {onFile ? <IconButton icon="edit" label={`Change projects for “${finding.title}”`} size="sm" onClick={() => onFile(finding)} /> : null}
        </p>
      ) : null}
      {shown.length > 0 ? (
        <ol className="slack-citations" aria-label="The Slack messages behind this finding">
          {shown.map((citation) => <SlackCitationItem key={citation.id} citation={citation} {...citationProps} />)}
        </ol>
      ) : null}
      {more.length > 0 ? (
        <details className="disclosure">
          <summary>
            <Icon name="chevron-down" size={16} />
            {pluralise(more.length, { one: 'Show # more message', other: 'Show # more messages' })}
          </summary>
          <div className="disclosure__body">
            <ol className="slack-citations">
              {more.map((citation) => <SlackCitationItem key={citation.id} citation={citation} {...citationProps} />)}
            </ol>
          </div>
        </details>
      ) : null}
      <div className="slack-finding__actions cluster">
        {addedTodo !== null ? (
          <p className="slack-finding__added text-sm">
            <Icon name="check" size={16} />
            <span>Added to your to-do list</span>
            <Link id={`${domId}-todo`} to={{ search: meetingTodoSearch(addedTodo) }} className="hit-area">
              View the to-do<span className="visually-hidden">: {finding.title}</span> <span aria-hidden="true">›</span>
            </Link>
          </p>
        ) : listedTodo !== null ? (
          <p className="slack-finding__added text-sm">
            <Icon name="check" size={16} />
            <span>Already on your to-do list</span>
            <Link to={{ search: meetingTodoSearch(listedTodo) }} className="hit-area">
              View the to-do<span className="visually-hidden">: {finding.title}</span> <span aria-hidden="true">›</span>
            </Link>
          </p>
        ) : canAddTodo ? (
          <Button variant="secondary" size="sm" icon="plus" onClick={() => onAddTodo(finding)}>
            Add as to-do…<span className="visually-hidden">: {finding.title}</span>
          </Button>
        ) : null}
        {finding.dismissed ? (
          <Button variant="secondary" size="sm" icon="undo" loading={busy.has(finding.id)} onClick={() => onRestore(finding)}>
            Restore<span className="visually-hidden">: {finding.title}</span>
          </Button>
        ) : (
          <Button variant="ghost" size="sm" icon="x" loading={busy.has(finding.id)} onClick={() => onDismiss(finding)}>
            Dismiss<span className="visually-hidden">: {finding.title}</span>
          </Button>
        )}
      </div>
    </li>
  );
}

function SideBlock({ label, side, links, timeZone }: { label: string; side: SlackSide; links: SlackMeetingLink[]; timeZone: string }) {
  return (
    <div className="slack-side stack stack--1">
      <p className="slack-side__label">
        {label}
        {side.at ? <> · <time dateTime={side.at} title={formatDateTime(side.at, { timeZone })}>{formatDate(side.at, { timeZone })}</time></> : null}
        {side.later ? ' · later' : null}
      </p>
      <p>{side.text}</p>
      {links.length > 0 ? <MeetingLinks links={links} lead="See " /> : null}
    </div>
  );
}

function MeetingLinks({ links, lead = 'In the meeting: ' }: { links: SlackMeetingLink[]; lead?: string }) {
  return (
    <p className="slack-meeting-links text-sm">
      <span className="secondary">{lead}</span>
      {links.map((link, index) => (
        <span key={`${link.label}-${index}`}>
          {index > 0 ? <span aria-hidden="true"> · </span> : null}
          {link.search !== null ? (
            <Link to={{ search: link.search, hash: link.hash ?? '' }} className="hit-area">{link.label}</Link>
          ) : (
            <span>{link.label} <span className="muted">(from an earlier summary)</span></span>
          )}
        </span>
      ))}
    </p>
  );
}

function SlackCitationItem({ citation, timeZone, canChooseChannels, onStopReading }: {
  citation: SlackCitationDto;
  timeZone: string;
  canChooseChannels: boolean;
  onStopReading: (citation: SlackCitationDto) => void;
}) {
  const author = slackCitationAuthor(citation);
  const parts = slackExcerptParts(citation);
  const href = slackPermalinkHref(citation.permalink);
  const described = slackCitationDescription(citation, timeZone);
  // Operations the reader cannot hold are not offered (permission-model 04-enforcement): leaving a
  // channel out is `slack_connect`'s, and a direct message is left out with the DM switch, not here.
  const canStop = canChooseChannels && citation.channelId !== null;
  return (
    <li className="slack-citation">
      <Avatar name={author} size="sm" decorative />
      <div className="slack-citation__body stack stack--1">
        <p className="slack-citation__meta">
          <span className="slack-citation__author">{author}</span>
          <span>{citation.conversationLabel}</span>
          <time dateTime={citation.postedAt}>{formatDateTime(citation.postedAt, { timeZone })}</time>
        </p>
        <blockquote className="slack-citation__excerpt">
          {parts.before}
          {parts.quote !== null ? <mark>{parts.quote}</mark> : null}
          {parts.after}
        </blockquote>
        {href || canStop ? (
          <div className="slack-citation__actions cluster">
            {href ? (
              // D9: Slack opens in a new tab, and the link says so to everyone, not only to the sighted.
              <LinkButton href={href} target="_blank" rel="noopener noreferrer" variant="link" size="sm" trailingIcon="external" className="hit-area">
                Open in Slack<span className="visually-hidden">: {described} (opens Slack in a new tab)</span>
              </LinkButton>
            ) : null}
            {canStop ? (
              <Menu
                label={`More for the ${described}`}
                entries={[{ id: 'stop-reading', label: `Stop reading ${citation.conversationLabel}`, icon: 'unplug', onSelect: () => onStopReading(citation) }]}
                trigger={(triggerProps) => <IconButton icon="more-horizontal" label={`More for the ${described}`} size="sm" {...triggerProps} />}
              />
            ) : null}
          </div>
        ) : null}
      </div>
    </li>
  );
}

// --- "Look in Slack…" ------------------------------------------------------------------------------

export interface LookInSlackDialogProps {
  open: boolean;
  meeting: MeetingDetail;
  ai: AiProvider;
  /** Whether a check asks the climate of what it reads (ADR 0019): `me.integrations.executive`, for the dialog's words. */
  climate: boolean;
  onClose: () => void;
  onQueued: (accepted: JobAcceptedResponse) => void;
}

/**
 * The per-run consent (owner rulings R7, R8): what a check may read — everything the person can see in
 * Slack, less the channels they told Calis not to read, counted here and changed in Connections — the
 * window, how it looks (judged searches first, every conversation last), where it goes and what is
 * kept. Its one choice is this run's direct messages and group chats, ticked when the person allows
 * them, which they may only untick. The server works out again what may be read and refuses anything
 * more. The tab mounts a fresh one for each opening (`key`), so it starts from this opening's preview
 * alone — nothing shown and nothing sendable until that preview has loaded.
 */
export function LookInSlackDialog({ open, meeting, ai, climate, onClose, onQueued }: LookInSlackDialogProps) {
  const formId = useId();
  const blockedId = useId();
  const path = `/api/meetings/${encodeURIComponent(meeting.id)}/slack-context`;
  const preview = useRequest<SlackContextPreviewResponse>(
    (signal) => (open ? api.get<SlackContextPreviewResponse>(`${path}/preview`, { signal }) : new Promise<never>(() => {})),
    [open, path],
  );
  const [dmsUnticked, setDmsUnticked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const data = preview.data;
  const timeZone = browserTimeZone();
  const dmsOffered = data?.directMessages.available === true;
  const includeDms = dmsOffered && !dmsUnticked;
  const blocked = data ? slackDialogBlockedReason({ channels: data.channels, searchReach: data.searchReach, includeDms, runsLeftToday: data.runsLeftToday, runsPerDay: data.runsPerDay }) : null;
  const windowText = data ? slackWindowText(data.window, meeting, timeZone) : null;
  const dmOption = data ? slackDirectMessageOption(data.directMessages.attendees, data.readableKinds) : null;

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    if (!data || blocked !== null || submitting) return;
    setSubmitting(true);
    setError(null);
    const body: PostSlackContextRequest = { includeDms };
    const result = await api.post<JobAcceptedResponse>(path, body);
    setSubmitting(false);
    if (!result.ok) {
      setError(result.correlationId ? `${inlineFailureText(result)} (reference ${result.correlationId})` : inlineFailureText(result));
      return;
    }
    onQueued(result.data);
  };

  return (
    <Dialog
      open={open}
      onClose={() => { if (!submitting) onClose(); }}
      // Why: unticking direct messages is the person's input; Escape or a tap outside the sheet asks
      // before throwing it away, and the next opening starts ticked again (03-components §4.1).
      unsavedChanges={!submitting && dmsUnticked}
      title="Look in Slack"
      description={`For “${meeting.title}”. Nothing is read until you press Look in Slack.`}
      actions={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button
            type="submit"
            form={formId}
            variant="primary"
            icon="search"
            loading={submitting}
            disabled={!data || blocked !== null}
            aria-describedby={blocked !== null ? blockedId : undefined}
          >
            Look in Slack
          </Button>
        </>
      }
    >
      {preview.status === 'loading' && !data ? <StateView kind="loading" label="Loading what Calis would read" minHeight="10rem" /> : null}
      {preview.status === 'error' && preview.error && !data ? (
        <FailureView failure={preview.error} what="what Calis would read" onRetry={preview.reload} compact minHeight="10rem" />
      ) : null}
      {data ? (
        <form id={formId} className="stack stack--3" onSubmit={(event) => void submit(event)} noValidate>
          <dl className="field-list">
            <dt>Window</dt>
            <dd>{windowText ?? 'Not known: the meeting’s time is not recorded.'}</dd>
            <dt>May read</dt>
            <dd>{slackDialogScope(data)}</dd>
            <dt>Left out</dt>
            <dd>
              {slackExcludedText(data.excluded)}{' '}
              <Link to={`${PATHS.connections}#slack-connection`} className="hit-area">Change this in Connections</Link>
            </dd>
          </dl>
          {/* One checkbox, not a group: its own label says "this check", so no fieldset or legend (03-components §3.2). */}
          {dmsOffered && dmOption ? (
            <Checkbox
              checked={!dmsUnticked}
              disabled={submitting}
              onChange={(event) => setDmsUnticked(!event.target.checked)}
              label={dmOption.label}
              description={dmOption.description}
            />
          ) : null}
          <p className="text-sm secondary">{slackCheckExplainer({ ai, retentionDays: data.retentionDays, climate })}</p>
          <p className="text-xs muted">{slackRunsLeftText(data.runsLeftToday, data.runsPerDay)}</p>
          {blocked !== null ? <p id={blockedId} className="field__hint">{blocked}</p> : null}
          {error ? <p className="field__error" role="alert">{error}</p> : null}
        </form>
      ) : null}
    </Dialog>
  );
}
